import { useLingui as _useLingui } from "../lib/i18n/useLingui";
import { Button, ICON_SIZE_MAP } from "../components/Button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuGroup, DropdownMenuItem, DropdownMenuTrigger } from "../components/Dropdown";
import { Icon } from "../components/Icon";
import { ChevronRight } from "../icons/v2-stroke";
import { cn } from "../lib";
import { cva } from "class-variance-authority";
import React from "react";
const DEFAULT_LABEL_TRUNCATE_LENGTH_MIDDLE = 15;
const DEFAULT_LABEL_TRUNCATE_LENGTH_END = 30;
const ELLIPSIS_STRING = "...";
const COLLAPSED_SEGMENT_LABEL = "…";
// The last segment may truncate when the trail's container is narrower than the trail, so the
// current location always ends visibly instead of being clipped. Needs a container with a
// definite width: inside a shrink-to-fit ancestor (a Radix scroll viewport's `display: table`
// wrapper, an inline-block) give the wrapper `w-0 min-w-full`.
const LAST_SEGMENT_CLASS = "min-w-0 shrink [&_span]:min-w-0 [&_span]:truncate";
const breadcrumbTextVariants = cva("", {
  variants: {
    isLast: {
      true: "text-foreground",
      false: "text-muted-foreground"
    },
    size: {
      xs: "",
      sm: ""
    },
    hasLighterFont: {
      true: "",
      false: ""
    }
  },
  compoundVariants: [{
    size: "xs",
    hasLighterFont: true,
    className: "text-xs"
  }, {
    size: "sm",
    hasLighterFont: true,
    className: "text-sm"
  }, {
    size: "xs",
    hasLighterFont: false,
    className: "label-xs"
  }, {
    size: "sm",
    hasLighterFont: false,
    className: "label-sm"
  }],
  defaultVariants: {
    size: "sm",
    hasLighterFont: true,
    isLast: false
  }
});
const isLinkItem = item => "href" in item && typeof item.href === "string";
const isButtonItem = item => "onClick" in item && typeof item.onClick === "function";
function BreadcrumbItemRenderer({
  item,
  isCollapsed,
  isLast,
  itemsHidden,
  size = "sm",
  buttonVariant = "ghost",
  hasLighterFont = true,
  truncateLengthMiddle = DEFAULT_LABEL_TRUNCATE_LENGTH_MIDDLE,
  truncateLengthEnd = DEFAULT_LABEL_TRUNCATE_LENGTH_END
}) {
  if (item.label === ELLIPSIS_STRING) {
    return /*#__PURE__*/React.createElement(DropdownMenu, null, /*#__PURE__*/React.createElement(DropdownMenuTrigger, {
      asChild: true
    }, /*#__PURE__*/React.createElement(Button, {
      variant: buttonVariant,
      label: ELLIPSIS_STRING,
      icon: item.icon,
      size: size,
      hasLighterFont: hasLighterFont
    })), /*#__PURE__*/React.createElement(DropdownMenuContent, {
      align: "start"
    }, /*#__PURE__*/React.createElement(DropdownMenuGroup, null, itemsHidden === null || itemsHidden === void 0 ? void 0 : itemsHidden.map((item, index) => /*#__PURE__*/React.createElement(DropdownMenuItem, {
      key: `breadcrumbs-hidden-${index}`,
      href: isLinkItem(item) ? item.href : undefined,
      onClick: isButtonItem(item) ? item.onClick : undefined,
      icon: item.icon,
      label: item.label
    })))));
  }
  const textClassName = breadcrumbTextVariants({
    isLast,
    size,
    hasLighterFont
  });
  if (isCollapsed) {
    if (isLinkItem(item) || isButtonItem(item)) {
      return /*#__PURE__*/React.createElement(Button, {
        href: isLinkItem(item) ? item.href : undefined,
        onClick: isButtonItem(item) ? item.onClick : undefined,
        variant: buttonVariant !== null && buttonVariant !== void 0 ? buttonVariant : "ghost-secondary",
        label: COLLAPSED_SEGMENT_LABEL,
        tooltip: item.label,
        size: size,
        hasLighterFont: hasLighterFont
      });
    }
    return /*#__PURE__*/React.createElement("div", {
      className: cn("px-2 py-1.5", textClassName),
      title: item.label
    }, COLLAPSED_SEGMENT_LABEL);
  }
  const truncatedLabel = truncateTextToLength(item.label, isLast ? truncateLengthEnd : truncateLengthMiddle);
  const isLabelTruncated = truncatedLabel !== item.label;
  const segmentClassName = isLast ? LAST_SEGMENT_CLASS : undefined;
  if (isLinkItem(item)) {
    return /*#__PURE__*/React.createElement(Button, {
      href: item.href,
      icon: item.icon,
      variant: buttonVariant !== null && buttonVariant !== void 0 ? buttonVariant : isLast ? "ghost" : "ghost-secondary",
      label: truncatedLabel,
      tooltip: isLabelTruncated ? item.label : undefined,
      size: size,
      hasLighterFont: hasLighterFont,
      className: segmentClassName
    });
  }
  if (isButtonItem(item)) {
    return /*#__PURE__*/React.createElement(Button, {
      onClick: item.onClick,
      icon: item.icon,
      variant: buttonVariant !== null && buttonVariant !== void 0 ? buttonVariant : isLast ? "ghost" : "ghost-secondary",
      label: truncatedLabel,
      tooltip: isLabelTruncated ? item.label : undefined,
      size: size,
      hasLighterFont: hasLighterFont,
      className: segmentClassName
    });
  }
  if (item.icon) {
    return /*#__PURE__*/React.createElement("div", {
      className: "shrink0 label-sm inline-flex h-9 items-center gap-2 border border-transparent px-3"
    }, /*#__PURE__*/React.createElement(Icon, {
      visual: item.icon,
      size: ICON_SIZE_MAP[size],
      className: cn("-mx-0.5")
    }), /*#__PURE__*/React.createElement("div", {
      className: textClassName
    }, item.label));
  }
  return /*#__PURE__*/React.createElement("div", {
    className: cn("px-2 py-1.5", isLast && "min-w-0 truncate", textClassName)
  }, item.label);
}
/**
 * Displays the user's location within a hierarchy as a trail of clickable segments,
 * driven by an `items` array. Long trails automatically collapse middle segments
 * into an ellipsis menu and truncate overflowing labels; `collapseIntermediates`
 * instead renders each middle segment as its own linked ellipsis, for narrow
 * containers. Use it to show and navigate
 * the path to the current page; for switching between sibling views rather than
 * levels of depth, use Tabs instead.
 * @summary Hierarchical navigation trail.
 */
export function Breadcrumbs({
  items,
  className,
  size = "sm",
  buttonVariant = "ghost",
  hasLighterFont = true,
  truncateLengthMiddle,
  truncateLengthEnd,
  collapseIntermediates = false
}) {
  const {
    itemsShown,
    itemsHidden
  } = items.reduce((acc, item, index) => {
    if (collapseIntermediates || items.length <= 5 || index < 2 || index >= items.length - 2) {
      acc.itemsShown.push(item);
    } else if (index === 2) {
      acc.itemsShown.push({
        label: ELLIPSIS_STRING
      });
      acc.itemsHidden.push(item);
    } else {
      acc.itemsHidden.push(item);
    }
    return acc;
  }, {
    itemsShown: [],
    itemsHidden: []
  });
  return /*#__PURE__*/React.createElement("div", {
    className: cn("flex min-w-0 flex-row items-center gap-0", className)
  }, itemsShown.map((item, index) => {
    const isLast = index === itemsShown.length - 1;
    return /*#__PURE__*/React.createElement("div", {
      key: `breadcrumbs-${index}`,
      className: cn("flex flex-row items-center gap-0", isLast ? "min-w-0 shrink" : "shrink-0")
    }, /*#__PURE__*/React.createElement(BreadcrumbItemRenderer, {
      item: item,
      isCollapsed: collapseIntermediates && index > 0 && !isLast,
      isLast: isLast,
      itemsHidden: itemsHidden,
      size: size,
      buttonVariant: buttonVariant,
      hasLighterFont: hasLighterFont,
      truncateLengthMiddle: truncateLengthMiddle,
      truncateLengthEnd: truncateLengthEnd
    }), isLast ? null : /*#__PURE__*/React.createElement(Icon, {
      visual: ChevronRight,
      className: "shrink-0 text-faint",
      size: size === "xs" ? "xs" : "sm"
    }));
  }));
}
function truncateTextToLength(text, length) {
  return text.length > length ? `${text.substring(0, length - 1)}${ELLIPSIS_STRING}` : text;
}

// Composable breadcrumb primitives.

/** Composable breadcrumb root: a nav landmark wrapping BreadcrumbItem children. */
export function Breadcrumb({
  children,
  className
}) {
  const {
    _: _t
  } = _useLingui();
  return /*#__PURE__*/React.createElement("nav", {
    "aria-label": _t(
    /** i18n */
    {
      id: "iu7tUI",
      message: "Breadcrumb"
    }),
    className: cn("flex flex-row items-center gap-0", className)
  }, children);
}
/** Composable wrapper for one breadcrumb segment inside a Breadcrumb. */
export function BreadcrumbItem({
  children,
  className
}) {
  return /*#__PURE__*/React.createElement("div", {
    className: cn("flex flex-row items-center", className)
  }, children);
}
/** Clickable breadcrumb segment rendered as a Button. */
export function BreadcrumbButton({
  label,
  onClick,
  variant = "ghost",
  size = "sm",
  icon
}) {
  return /*#__PURE__*/React.createElement(Button, {
    label: label,
    onClick: onClick,
    variant: variant,
    size: size,
    icon: icon,
    hasLighterFont: true
  });
}
/** Non-interactive current-page segment (`aria-current="page"`). */
export function BreadcrumbPage({
  children,
  className
}) {
  return /*#__PURE__*/React.createElement("span", {
    "aria-current": "page",
    className: cn("inline-flex h-9 items-center px-3", breadcrumbTextVariants({
      isLast: true,
      size: "sm",
      hasLighterFont: true
    }), className)
  }, children);
}

/** Chevron separator between breadcrumb segments. */
export function BreadcrumbSeparator({
  className
}) {
  return /*#__PURE__*/React.createElement(Icon, {
    "aria-hidden": "true",
    visual: ChevronRight,
    className: cn("text-faint", className),
    size: "sm"
  });
}
//# sourceMappingURL=Breadcrumbs.js.map